import { LoadingButton } from "@mui/lab";
import {
  Backdrop,
  Box,
  CircularProgress,
  FormHelperText,
  Grid,
  Link,
  Typography,
  capitalize,
} from "@mui/material";

import { useSubscription } from "utils/useSubscription";

import { StripeCustomer } from "../schema";

interface Props {
  onConfirm: () => void;
  confirmLoading: boolean;
  customer: StripeCustomer;
  noNeedsToPay: boolean;
}

const ConfirmPaymentForm = ({
  onConfirm,
  confirmLoading,
  customer,
  noNeedsToPay,
}: Props) => {
  const { handleManagePlan, checkoutLoading } = useSubscription();

  const redirectToBillingPay = () => {
    handleManagePlan();
  };

  const getCardIcon = () => {
    if (customer?.payment_method?.card?.brand === "visa") {
      return "fa-brands fa-cc-visa";
    } else if (customer?.payment_method?.card?.brand === "mastercard") {
      return "fa-brands fa-cc-mastercard";
    } else {
      return "fa-solid fa-credit-card";
    }
  };

  const displayCard = () => {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography gutterBottom>
          <strong> Your current payment method</strong>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Box>
            <Box component="i" className={getCardIcon()} sx={{ mr: 1 }} />
            {capitalize(customer?.payment_method?.card?.brand || "")} ****
            {customer?.payment_method?.card?.last4}
          </Box>

          <Box>
            Expires {customer?.payment_method?.card?.exp_month}/
            {customer?.payment_method?.card?.exp_year}
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Grid item xs={12} sx={{ mb: 3 }}>
        {checkoutLoading && (
          <Backdrop
            sx={{
              backgroundColor: "#fff",
              zIndex: 9999,
            }}
            open={checkoutLoading}
          >
            <CircularProgress />
          </Backdrop>
        )}

        <Typography variant="h4" sx={{ mb: 2 }}>
          Confirm Your Updates
        </Typography>

        {noNeedsToPay ? (
          <Typography>
            Press <strong>Confirm</strong> below to upgrade to your new
            subscription. You do not need to pay anything at this time.
          </Typography>
        ) : (
          <Box>
            {customer?.payment_method && displayCard()}
            We will be charging your payment form on file displayed above with
            the amount on the invoice. <br />
            If you want to update your default payment method, click{" "}
            <Link
              sx={{ textDecoration: "none", cursor: "pointer" }}
              onClick={redirectToBillingPay}
            >
              <strong>here</strong>
            </Link>
          </Box>
        )}

        <LoadingButton
          loading={confirmLoading}
          variant="contained"
          disableElevation
          sx={{ my: 3 }}
          onClick={onConfirm}
        >
          Confirm
        </LoadingButton>

        <FormHelperText>
          By confirming your new subscription, you agree to{" "}
          <Link
            href="https://www.onbento.com/terms-of-services"
            target="_blank"
            sx={{ cursor: "pointer" }}
          >
            <strong>Bento's Terms of Service</strong>
          </Link>{" "}
          and{" "}
          <Link
            sx={{ cursor: "pointer" }}
            href="https://www.onbento.com/privacy-policy"
            target="_blank"
          >
            <strong>Privacy Policy</strong>
          </Link>
          .
        </FormHelperText>
      </Grid>
    </>
  );
};

export default ConfirmPaymentForm;
